
import { Lead, leadStatusLabels, leadPriorityLabels } from "./types";
import { formatDistanceToNow } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  ExternalLink, 
  Calendar, 
  BarChart4 
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface LeadsTableProps {
  leads: Lead[];
}

export const LeadsTable = ({ leads }: LeadsTableProps) => {
  const navigate = useNavigate();
  
  if (leads.length === 0) {
    return (
      <div className="text-center p-8 border rounded-lg bg-muted/20">
        <h3 className="text-lg font-medium">No leads found</h3>
        <p className="text-muted-foreground mt-2">
          Add your first lead using the "Add Lead" button above.
        </p>
      </div>
    );
  }
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "low": return "bg-green-100 text-green-800";
      case "medium": return "bg-blue-100 text-blue-800";
      case "high": return "bg-amber-100 text-amber-800"; 
      case "urgent": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case "new": return "bg-gray-100 text-gray-800";
      case "contacted": return "bg-blue-100 text-blue-800";
      case "meeting": return "bg-purple-100 text-purple-800";
      case "proposal": return "bg-amber-100 text-amber-800";
      case "negotiating": return "bg-orange-100 text-orange-800";
      case "won": return "bg-green-100 text-green-800";
      case "lost": return "bg-red-100 text-red-800";
      case "onhold": return "bg-slate-100 text-slate-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>Industry</TableHead>
            <TableHead className="hidden md:table-cell">Next Contact</TableHead>
            <TableHead className="hidden md:table-cell">Last Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((lead) => (
            <TableRow 
              key={lead.id}
              className="cursor-pointer hover:bg-muted/40"
              onClick={() => navigate(`/leads/${lead.id}`)}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  {lead.name}
                  {lead.website && (
                    <a
                      href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      <span className="sr-only">Visit website</span>
                    </a>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={getStatusColor(lead.status)}>
                  {leadStatusLabels[lead.status as keyof typeof leadStatusLabels]}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={getPriorityColor(lead.priority)}>
                  {leadPriorityLabels[lead.priority as keyof typeof leadPriorityLabels]}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <BarChart4 className="h-4 w-4 text-muted-foreground" />
                  <span>{lead.lead_score}</span>
                </div>
              </TableCell>
              <TableCell>{lead.industry || "—"}</TableCell>
              <TableCell className="hidden md:table-cell">
                {lead.next_contact_at ? (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {formatDistanceToNow(new Date(lead.next_contact_at), { addSuffix: true })}
                  </div>
                ) : "—"}
              </TableCell>
              <TableCell className="hidden md:table-cell text-muted-foreground">
                {formatDistanceToNow(new Date(lead.updated_at), { addSuffix: true })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
